import { getLocaleFirstDayOfWeek } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  LOCALE_ID,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  explicitToImplicitTz,
  implicitToExplicitTz,
} from '@bmat/angular-luxon';
import { DateTime, Zone } from 'luxon';
import { OwlDateTimeComponent } from 'ng-pick-datetime';
import { BehaviorSubject } from 'rxjs';

export interface DateTimeChangeEvent {
  input: HTMLInputElement;
  source: OwlDateTimeComponent<Date>;
  value: Date;
}

export type InnerValue = Date | null;

function coerceDate(v: string, zone: string | Zone = 'utc'): InnerValue {
  if (!v) {
    return null;
  }

  const instant = DateTime.fromISO(v, { zone });
  if (!instant.isValid) {
    return null;
  }

  return explicitToImplicitTz(instant);
}

// dateTimeChange
@Component({
  selector: 'bm-moment-in-bound',
  templateUrl: 'moment-in-bound.component.html',
  styleUrls: ['moment-in-bound.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => MomentInBoundComponent),
      multi: true,
    },
  ],
  // tslint:disable-next-line:use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
})
/**
 * Status Filter
 */
export class MomentInBoundComponent implements ControlValueAccessor {
  public innerValue$ = new BehaviorSubject<InnerValue>(null);

  @Input() public set value(v: string) {
    this.innerValue$.next(coerceDate(v, this.timezone));
  }

  // Input HTMLElement values
  @Input('aria-label') public ariaLabel!: string;
  @Input('aria-labelledby') public ariaLabelledby!: string;
  @Input() public disabled!: boolean;
  @Input() public name!: string;
  @Input() public readonly!: boolean;
  @Input() public required!: string;
  @Input() public timezone!: string | Zone;

  @Input() public set startTime(v: string) {
    const next = new Date(v);
    if (!Number.isNaN(next.getTime())) {
      this.calendarMin = next;
    }
  }

  @Input() public set endTime(v: string) {
    const next = new Date(v);
    if (!Number.isNaN(next.getTime())) {
      this.calendarMax = next;
    }
  }

  /**
   * Emit a RFC 3339 Datetime string
   */
  @Output() public momentSelect = new EventEmitter<null | string>();

  public firstDayOfWeek: number;
  public calendarMin!: Date;
  public calendarMax!: Date;

  private _onChangeCb!: (_: string | null) => void;
  private _onTouchedCb!: () => void;

  constructor(@Inject(LOCALE_ID) locale: string) {
    this.firstDayOfWeek = getLocaleFirstDayOfWeek(locale);
  }

  public onDateTimeChange(e: DateTimeChangeEvent) {
    let nextValue: null | string = null;
    if (e.value && !Number.isNaN(e.value.getTime())) {
      nextValue = implicitToExplicitTz(e.value, this.timezone || 'utc').toISO();
    }

    if (this._onChangeCb) {
      this._onChangeCb(nextValue);
    }

    this.momentSelect.emit(nextValue);
  }

  // ControlValueAccessor Methods
  public writeValue(v: string) {
    this.innerValue$.next(coerceDate(v, this.timezone));
  }

  public registerOnChange(fn: (_: string | null) => void) {
    this._onChangeCb = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouchedCb = fn;
  }

  public setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Other methods
  public onFocusin() {
    if (this._onTouchedCb) {
      this._onTouchedCb();
    }
  }
}
